/**
 * Detect missing comma / JOIN between table references in FROM.
 *
 * Oracle often misreports this as ORA-00907 ("missing right parenthesis").
 * This is a real FROM-list parse (token depth, aliases, subqueries) — not a
 * single-regex quick fix for one bad query.
 *
 * Valid:
 *   FROM t1 a, t2 b
 *   FROM t1 a JOIN t2 b ON ...
 *   FROM t1 t2          -- t2 is alias of t1
 * Invalid (this check):
 *   FROM t1 a
 *        t2 b           -- second table after alias, no comma/JOIN
 *   FROM t1 a t2 b
 */

const JOIN_LEAD = new Set([
  'JOIN',
  'INNER',
  'LEFT',
  'RIGHT',
  'FULL',
  'CROSS',
  'NATURAL',
  'OUTER',
]);

const FROM_END = new Set([
  'WHERE',
  'GROUP',
  'ORDER',
  'HAVING',
  'UNION',
  'MINUS',
  'INTERSECT',
  'FETCH',
  'OFFSET',
  'CONNECT',
  'START',
  'FOR', // FOR UPDATE
]);

const NOT_ALIAS = new Set([
  ...JOIN_LEAD,
  ...FROM_END,
  'ON',
  'USING',
  'AS',
  'PARTITION',
  'PIVOT',
  'UNPIVOT',
  'MODEL',
  'SAMPLE',
  'VERSIONS',
  'ASOF',
  'BY',
  'UPDATE',
  'SELECT',
  'WITH',
  'SET',
]);

/**
 * @typedef {{ type: 'ident'|'punct'|'number', value: string, index: number, upper: string }} SqlToken
 */

/**
 * Tokenize SQL enough for FROM-list structure (skips strings/comments; tracks punct).
 * @param {string} sql
 * @returns {SqlToken[]}
 */
export function tokenizeSqlLite(sql) {
  const tokens = [];
  if (!sql) return tokens;
  let i = 0;
  const s = sql;
  const n = s.length;

  while (i < n) {
    const c = s[i];

    if (/\s/.test(c)) {
      i++;
      continue;
    }

    // line comment
    if (c === '-' && s[i + 1] === '-') {
      i += 2;
      while (i < n && s[i] !== '\n' && s[i] !== '\r') i++;
      continue;
    }

    // block comment
    if (c === '/' && s[i + 1] === '*') {
      i += 2;
      while (i < n && !(s[i] === '*' && s[i + 1] === '/')) i++;
      i = Math.min(n, i + 2);
      continue;
    }

    // quoted string
    if (c === "'") {
      i++;
      while (i < n) {
        if (s[i] === "'" && s[i + 1] === "'") {
          i += 2;
          continue;
        }
        if (s[i] === "'") {
          i++;
          break;
        }
        i++;
      }
      continue;
    }

    // quoted identifier
    if (c === '"') {
      const start = i;
      i++;
      while (i < n) {
        if (s[i] === '"' && s[i + 1] === '"') {
          i += 2;
          continue;
        }
        if (s[i] === '"') {
          i++;
          break;
        }
        i++;
      }
      const value = s.slice(start, i);
      tokens.push({ type: 'ident', value, index: start, upper: value.replace(/"/g, '').toUpperCase() });
      continue;
    }

    // punctuation
    if (',()@.'.includes(c) || c === ';') {
      tokens.push({ type: 'punct', value: c, index: i, upper: c });
      i++;
      continue;
    }

    // number
    if (/[0-9]/.test(c)) {
      const start = i;
      while (i < n && /[0-9.]/.test(s[i])) i++;
      const value = s.slice(start, i);
      tokens.push({ type: 'number', value, index: start, upper: value });
      continue;
    }

    // identifier / keyword
    if (/[A-Za-z_$#]/.test(c)) {
      const start = i;
      while (i < n && /[A-Za-z0-9_$#]/.test(s[i])) i++;
      // dblink: keep as separate @ handling via punct
      const value = s.slice(start, i);
      tokens.push({
        type: 'ident',
        value,
        index: start,
        upper: value.toUpperCase(),
      });
      continue;
    }

    i++;
  }

  return tokens;
}

function offsetToLineCol(sql, offset) {
  const before = sql.slice(0, Math.max(0, offset));
  const lines = before.split(/\r?\n/);
  return { line: lines.length, column: (lines[lines.length - 1] || '').length + 1 };
}

function isIdent(tok) {
  return tok && tok.type === 'ident';
}

function skipJoinLead(tokens, i) {
  // INNER OUTER LEFT RIGHT FULL CROSS NATURAL JOIN (any order of lead words then JOIN)
  let j = i;
  let sawJoin = false;
  while (j < tokens.length && isIdent(tokens[j]) && JOIN_LEAD.has(tokens[j].upper)) {
    if (tokens[j].upper === 'JOIN') sawJoin = true;
    j++;
    if (sawJoin) break;
  }
  // LEFT OUTER JOIN — continue until JOIN if we haven't seen it
  while (!sawJoin && j < tokens.length && isIdent(tokens[j]) && JOIN_LEAD.has(tokens[j].upper)) {
    if (tokens[j].upper === 'JOIN') {
      sawJoin = true;
      j++;
      break;
    }
    j++;
  }
  return sawJoin ? j : i;
}

/**
 * Parse one table reference starting at i: [schema.]table [@dblink] [AS] [alias]
 * Skips parenthesized subquery (... ) [alias]
 * @returns {{ next: number, tableName: string, alias: string|null, tableIndex: number } | null}
 */
function parseTableRef(tokens, i) {
  if (i >= tokens.length) return null;

  // subquery
  if (tokens[i].type === 'punct' && tokens[i].value === '(') {
    let depth = 1;
    let j = i + 1;
    while (j < tokens.length && depth > 0) {
      if (tokens[j].type === 'punct' && tokens[j].value === '(') depth++;
      else if (tokens[j].type === 'punct' && tokens[j].value === ')') depth--;
      j++;
    }
    let alias = null;
    if (j < tokens.length && isIdent(tokens[j]) && tokens[j].upper === 'AS') j++;
    if (j < tokens.length && isIdent(tokens[j]) && !NOT_ALIAS.has(tokens[j].upper)) {
      alias = tokens[j].value;
      j++;
    }
    return { next: j, tableName: '(subquery)', alias, tableIndex: i };
  }

  if (!isIdent(tokens[i])) return null;
  if (FROM_END.has(tokens[i].upper) || JOIN_LEAD.has(tokens[i].upper)) return null;

  const tableIndex = tokens[i].index;
  let j = i;
  let tableName = tokens[j].value;
  j++;

  // schema.table
  if (j + 1 < tokens.length && tokens[j].type === 'punct' && tokens[j].value === '.' && isIdent(tokens[j + 1])) {
    tableName = `${tableName}.${tokens[j + 1].value}`;
    j += 2;
  }

  // @dblink
  if (j + 1 < tokens.length && tokens[j].type === 'punct' && tokens[j].value === '@' && isIdent(tokens[j + 1])) {
    tableName = `${tableName}@${tokens[j + 1].value}`;
    j += 2;
  }

  // optional alias
  let alias = null;
  if (j < tokens.length && isIdent(tokens[j]) && tokens[j].upper === 'AS') {
    j++;
    if (j < tokens.length && isIdent(tokens[j]) && !NOT_ALIAS.has(tokens[j].upper)) {
      alias = tokens[j].value;
      j++;
    }
  } else if (j < tokens.length && isIdent(tokens[j]) && !NOT_ALIAS.has(tokens[j].upper)) {
    // single identifier after table = alias in Oracle (FROM t1 t2)
    alias = tokens[j].value;
    j++;
  }

  return { next: j, tableName, alias, tableIndex };
}

/**
 * Walk FROM clause(s) and collect table refs + first missing-join error if any.
 * @returns {{ refs: Array<{tableName:string,alias:string|null,tableIndex:number}>, missingJoin: object|null }}
 */
function walkFromClause(sql) {
  const refs = [];
  let missingJoin = null;
  if (!sql || typeof sql !== 'string') return { refs, missingJoin };
  const tokens = tokenizeSqlLite(sql);

  for (let start = 0; start < tokens.length; start++) {
    if (!(isIdent(tokens[start]) && tokens[start].upper === 'FROM')) continue;

    let i = start + 1;
    let previous = null;

    while (i < tokens.length) {
      if (isIdent(tokens[i]) && FROM_END.has(tokens[i].upper)) break;
      if (tokens[i].type === 'punct' && tokens[i].value === ';') break;

      const afterJoin = skipJoinLead(tokens, i);
      if (afterJoin > i) {
        i = afterJoin;
        const ref = parseTableRef(tokens, i);
        if (!ref) break;
        refs.push(ref);
        previous = ref;
        i = ref.next;
        i = skipJoinCondition(tokens, i);
        continue;
      }

      if (tokens[i].type === 'punct' && tokens[i].value === ',') {
        i++;
        previous = null;
        const ref = parseTableRef(tokens, i);
        if (!ref) break;
        refs.push(ref);
        previous = ref;
        i = ref.next;
        continue;
      }

      const ref = parseTableRef(tokens, i);
      if (!ref) {
        if (isIdent(tokens[i]) && (tokens[i].upper === 'ON' || tokens[i].upper === 'USING')) {
          i = skipJoinCondition(tokens, i);
          continue;
        }
        break;
      }

      if (previous && !missingJoin) {
        const { line, column } = offsetToLineCol(sql, ref.tableIndex);
        const prevLabel = previous.alias
          ? `${previous.tableName} ${previous.alias}`
          : previous.tableName;
        missingJoin = {
          error: `Missing comma or JOIN between tables in FROM: after "${prevLabel}" found "${ref.tableName}".`,
          suggestion:
            `Use a comma (FROM ${previous.tableName}${previous.alias ? ' ' + previous.alias : ''}, ${ref.tableName}${ref.alias ? ' ' + ref.alias : ''}) ` +
            `or an ANSI join (FROM ${previous.tableName}${previous.alias ? ' ' + previous.alias : ''} JOIN ${ref.tableName}${ref.alias ? ' ' + ref.alias : ''} ON ...). ` +
            `Oracle often misreports this as ORA-00907 (missing right parenthesis).`,
          line,
          column,
          previousTable: previous.tableName,
          nextTable: ref.tableName,
          index: ref.tableIndex,
        };
      }

      refs.push(ref);
      previous = ref;
      i = ref.next;
    }
  }

  return { refs, missingJoin };
}

/**
 * Table/alias list from FROM (for ambiguous column guidance).
 * @returns {Array<{ tableName: string, alias: string|null, qualifier: string }>}
 */
export function extractFromTableRefs(sql) {
  const { refs } = walkFromClause(sql);
  return refs.map((r) => ({
    tableName: r.tableName,
    alias: r.alias,
    qualifier: r.alias || r.tableName,
  }));
}

/**
 * Find missing comma/JOIN between FROM table references.
 */
export function findMissingFromJoin(sql) {
  return walkFromClause(sql).missingJoin;
}

/** Skip ON/USING condition until JOIN lead, comma, or FROM terminator at paren depth 0. */
function skipJoinCondition(tokens, i) {
  if (i >= tokens.length) return i;
  if (!(isIdent(tokens[i]) && (tokens[i].upper === 'ON' || tokens[i].upper === 'USING'))) {
    return i;
  }
  i++; // consume ON/USING
  let depth = 0;
  while (i < tokens.length) {
    const t = tokens[i];
    if (t.type === 'punct' && t.value === '(') {
      depth++;
      i++;
      continue;
    }
    if (t.type === 'punct' && t.value === ')') {
      depth--;
      i++;
      continue;
    }
    if (depth === 0) {
      if (t.type === 'punct' && t.value === ',') break;
      if (t.type === 'punct' && t.value === ';') break;
      if (isIdent(t) && FROM_END.has(t.upper)) break;
      if (isIdent(t) && JOIN_LEAD.has(t.upper)) break;
    }
    i++;
  }
  return i;
}
